// 常駐活動輪盤
'use client';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    popState,
    setCampaignSpinModal,
    setLoginModal,
    setCampaignUserInfo,
} from '../../slices/popSlice';
import { userState } from '@/slices/userSlice';
import campaignClient from '@/common/apiClient/campaignClient';

import CampaignRoulette from './CampaignRoulette';
import CampaignSpinInfo from './CampaignSpinInfo';
import CampaignSpinInit from './CampaignSpinInit';
import CampaignSpinDisable from './CampaignSpinDisable';
import CampaignSpinWinning from './CampaignSpinWinning';

const CampaignSpin = ({ roulettes, drawTime, spinSlug, bonus }) => {
    const dispatch = useDispatch();
    const { user } = useSelector(userState);
    const { showCampaignSpin, campaignUserInfo } = useSelector(popState);

    const [isSpinning, setIsSpinning] = useState(false); // 是否啟動
    const [winningGrid, setWinningGrid] = useState(null); // 轉盤結果 (0-16);
    const [winningAmount, setWinningAmount] = useState(0); // 轉盤獎勵
    const [rotateAngle, setRotateAngle] = useState(79); // 選轉角度

    // 取得玩家活動資訊
    const getUserInfo = async () => {
        try {
            const result = await campaignClient.getCampaignUserInfo(spinSlug);

            // completedTaskIds: [] 已完成任務
            // drawLogs: [] 中獎紀錄
            // leftSpinTimes: 0 剩餘轉盤次數
            // spinAwards: [] 轉盤獎勵
            // tickets: 0 // 抽獎券數

            if (result)
                dispatch(
                    setCampaignUserInfo({
                        ...result,
                        spinAwards: result?.spinAwards?.reverse() || [],
                    })
                );
        } catch (e) {
            console.error(e);
        }
    };

    // 處理轉盤旋轉角度
    const handleRotate = (grid) => {
        // const fullRotations = 10 + Math.floor(Math.random() * 3); // 增加一個隨機的全轉圈數，讓結果更不固定
        const targetAngle = -(7 * 360 + grid * (360 / 17)); // 計算旋轉目標角度
        const finalAngle =
            targetAngle + (rotateAngle === 79 ? 79 : rotateAngle - (rotateAngle % 360) + 79);
        setIsSpinning(true);
        setRotateAngle(finalAngle);
    };

    // 停止旋轉 => 更新抽獎券、轉盤次數
    const handleStopSpin = () => {
        setIsSpinning(false);

        // 抽獎券數＋n
        handleSpinAndDrawAmount('tickets', roulettes?.[winningGrid]?.amount);

        // 取得新的玩家資訊
        getUserInfo();
    };

    // 處理中獎相關
    const handleWinningGrid = (grid) => {
        setWinningAmount(roulettes?.[grid]?.amount);
        setWinningGrid(grid);
    };

    // 處理剩餘次數、抽獎券數
    const handleSpinAndDrawAmount = (type, amount) => {
        // 剩餘次數 -1、抽獎券數 + n
        const newUserInfo = { ...campaignUserInfo };
        if (type === 'tickets') newUserInfo.tickets += amount;
        newUserInfo.leftSpinTimes -= 1;

        // 更新玩家資訊
        dispatch(setCampaignUserInfo(newUserInfo));
    };

    // 啟動轉盤
    const onStart = async () => {
        // 需要登入
        if (!user?.uid) {
            dispatch(setLoginModal(true));
            return;
        }

        if (campaignUserInfo?.leftSpinTimes > 0) {
            // 剩餘次數大於零才能啟動
            try {
                // 先取得轉盤結果
                const result = await campaignClient.spin(spinSlug);
                if (result?.status === 'success') {
                    setWinningGrid(null);

                    // 剩餘次數 -1
                    handleSpinAndDrawAmount('leftSpinTimes');

                    // 轉盤結果 => 帶入旋轉角度、判斷中獎獎勵
                    const grid = result?.winningGrid - 1;

                    // 處理中獎相關
                    handleWinningGrid(grid);

                    // 處理轉盤旋轉角度
                    handleRotate(grid);
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            setWinningGrid(null);
        }
    };

    // 關閉轉盤
    const onClose = () => {
        if (isSpinning) return;
        dispatch(setCampaignSpinModal(false));
    };

    return (
        <div id="campaign-spin-modal" className={`modal ${showCampaignSpin ? 'act' : ''}`}>
            <div
                className="rounded-[22px] max-h-[99vh] noScrollY relative z-[2] w-[96%] max-w-[400px] border-1 border-[#fff] border-opacity-[.3] bg-[#101C2C] px-[18px] pb-[24px] overflow-hidden
                flex flex-col usx:pb-[18px]"
            >
                {/* 上半部輪盤、遊戲資訊 */}
                <div className="relative">
                    {/* 玩家資訊 抽獎券數、轉盤次數 */}
                    <CampaignSpinInfo
                        ticketsAmount={campaignUserInfo?.tickets}
                        leftSpinTimes={campaignUserInfo?.leftSpinTimes}
                    />

                    {/* 輪盤 */}
                    <CampaignRoulette
                        roulettes={roulettes}
                        rotateAngle={rotateAngle}
                        handleStopSpin={handleStopSpin}
                    />
                </div>

                {/* 下半部按鈕文案 */}
                <div
                    className={`mt-[30px] text-center px-[30px] usm:mt-[20px] usm:px-[10px] uxs:!px-0 uxs:!mt-[10px] ${
                        isSpinning ? 'h-[236px]' : ''
                    }`}
                >
                    {/* 啟動中 */}
                    {isSpinning && (
                        <p className="relative text-bd1m text-[#fff] h-full flex items-center justify-center usm:bottom-[20px]">
                            獎券已投放，轉盤抽取中...
                        </p>
                    )}

                    {/* 未啟動 */}
                    {!isSpinning &&
                        (winningGrid && winningGrid > -1 ? (
                            //  中獎畫面 (一般、bonus)
                            <CampaignSpinWinning
                                winningGrid={winningGrid}
                                winningAmount={winningAmount}
                                drawTime={drawTime}
                                onStart={onStart}
                                onClose={onClose}
                                isSpinning={isSpinning}
                                bonus={bonus}
                            />
                        ) : campaignUserInfo?.leftSpinTimes > 0 ? (
                            // 有剩餘次數 => 可以啟動
                            <CampaignSpinInit
                                onStart={onStart}
                                onClose={onClose}
                                isSpinning={isSpinning}
                            />
                        ) : (
                            // 轉盤次數不足
                            <CampaignSpinDisable onClose={onClose} isSpinning={isSpinning} />
                        ))}
                </div>
            </div>

            {/* 關閉用 */}
            {/* <div className="close-bg" /> */}
        </div>
    );
};

export default CampaignSpin;
